import { useCallback, useEffect, useRef, useState } from "react";
import {
  fetchAllDocs,
  fetchPage,
  fetchTotalCount,
} from "../utils/paginatedQuery";

/**
 * Hybrid pagination for Blogs / News / Insights list views.
 *
 *   - Browse mode (no search): server-side cursor pagination. Each
 *     page click = pageSize reads. Status filter applied server-side.
 *   - Search mode (search is non-empty): fetch every doc in the
 *     collection ONCE per session (cached in a ref), then filter +
 *     paginate client-side. First search pays N reads; subsequent
 *     searches and status-filter toggles within the same session are free.
 *
 * Trade-off: zero extra reads when browsing only, one-time fetch-all
 * cost when the user opens a search query. The client-side filter
 * supports case-insensitive substring across title, description, and
 * author (where present) — much better UX than the previous Firestore
 * prefix-only search.
 */

// Fields searched client-side in search mode. Title and description
// always exist on blogs/news/insights; author only on blogs/news.
const SEARCH_FIELDS = ["title", "description", "author"];

function buildSearchableText(doc) {
  return SEARCH_FIELDS.map((f) => doc[f] || "")
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function dateDescSort(a, b) {
  const ta = new Date(a?.date || a?.createdAt || 0).getTime() || 0;
  const tb = new Date(b?.date || b?.createdAt || 0).getTime() || 0;
  return tb - ta;
}

export function usePaginatedDocs({
  schema,
  pageSize = 12,
  search = "",
  statusFilter = "all",
}) {
  const isSearchMode = (search || "").trim() !== "";

  const [docs, setDocs] = useState([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isPaging, setIsPaging] = useState(false);
  const [error, setError] = useState(null);

  // Browse-mode cursor stack. cursorsRef.current[i] = startAfter cursor
  // for page i+1. Index 0 is always null.
  const cursorsRef = useRef([null]);

  // Search-mode cache. Survives status-filter toggles and re-searches
  // within the same hook lifetime. Re-fetches only when collection
  // changes (different schema key).
  const allDocsRef = useRef(null); // { key, docs }

  const reqIdRef = useRef(0);

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  // ─── Browse mode ───────────────────────────────────────────────────

  const fetchAtBrowse = useCallback(
    async (targetPage) => {
      const myReqId = ++reqIdRef.current;
      const isInitial =
        cursorsRef.current.length === 1 && targetPage === 1;
      if (isInitial) setIsLoading(true);
      else setIsPaging(true);
      setError(null);
      try {
        let highestKnown = cursorsRef.current.length - 1;
        while (highestKnown < targetPage - 1) {
          const cursor = cursorsRef.current[highestKnown];
          const result = await fetchPage({
            collectionName: schema.key,
            search: "",
            statusFilter,
            schema,
            pageSize,
            cursor,
          });
          if (myReqId !== reqIdRef.current) return;
          if (result.snaps.length === 0) break;
          cursorsRef.current.push(result.snaps[result.snaps.length - 1]);
          highestKnown += 1;
        }

        const cursor = cursorsRef.current[targetPage - 1] ?? null;
        const result = await fetchPage({
          collectionName: schema.key,
          search: "",
          statusFilter,
          schema,
          pageSize,
          cursor,
        });
        if (myReqId !== reqIdRef.current) return;

        if (
          result.snaps.length === pageSize &&
          cursorsRef.current.length === targetPage
        ) {
          cursorsRef.current.push(result.snaps[result.snaps.length - 1]);
        }

        setDocs(result.docs);
        setPage(targetPage);
      } catch (err) {
        if (myReqId !== reqIdRef.current) return;
        console.error("[usePaginatedDocs/browse]", err);
        setError(err);
      } finally {
        if (myReqId === reqIdRef.current) {
          setIsLoading(false);
          setIsPaging(false);
        }
      }
    },
    [schema, pageSize, statusFilter],
  );

  const refreshBrowseCount = useCallback(async () => {
    try {
      const count = await fetchTotalCount({
        collectionName: schema.key,
        search: "",
        statusFilter,
        schema,
      });
      setTotalCount(count);
      return count;
    } catch (err) {
      console.error(err);
      return null;
    }
  }, [schema, statusFilter]);

  // ─── Search mode ───────────────────────────────────────────────────

  const ensureAllDocs = useCallback(async () => {
    if (allDocsRef.current && allDocsRef.current.key === schema.key) {
      return allDocsRef.current.docs;
    }
    const docs = await fetchAllDocs({ collectionName: schema.key });
    allDocsRef.current = { key: schema.key, docs };
    return docs;
  }, [schema.key]);

  const filterCachedDocs = useCallback(
    (allDocs) => {
      const q = (search || "").trim().toLowerCase();
      return allDocs.filter((d) => {
        // Status filter (client-side over the cache).
        if (statusFilter !== "all") {
          const isPub = d[schema.statusField] === schema.publishedValue;
          if (statusFilter === "published" && !isPub) return false;
          if (statusFilter === "draft" && isPub) return false;
        }
        if (!q) return true;
        return buildSearchableText(d).includes(q);
      });
    },
    [search, statusFilter, schema],
  );

  const fetchAtSearch = useCallback(
    async (targetPage) => {
      const myReqId = ++reqIdRef.current;
      const hasCached =
        allDocsRef.current && allDocsRef.current.key === schema.key;
      if (!hasCached) setIsLoading(true);
      else setIsPaging(true);
      setError(null);
      try {
        const all = await ensureAllDocs();
        if (myReqId !== reqIdRef.current) return;

        const filtered = filterCachedDocs(all).sort(dateDescSort);
        const total = filtered.length;
        const newTotalPages = Math.max(1, Math.ceil(total / pageSize));
        const safeTarget = Math.min(Math.max(1, targetPage), newTotalPages);
        const start = (safeTarget - 1) * pageSize;
        const pageDocs = filtered.slice(start, start + pageSize);

        setDocs(pageDocs);
        setTotalCount(total);
        setPage(safeTarget);
      } catch (err) {
        if (myReqId !== reqIdRef.current) return;
        console.error("[usePaginatedDocs/search]", err);
        setError(err);
      } finally {
        if (myReqId === reqIdRef.current) {
          setIsLoading(false);
          setIsPaging(false);
        }
      }
    },
    [schema.key, pageSize, ensureAllDocs, filterCachedDocs],
  );

  // ─── Effect: load when query shape changes ─────────────────────────

  useEffect(() => {
    if (isSearchMode) {
      // Search mode: filter + paginate the cache (fetch if first time).
      fetchAtSearch(1);
    } else {
      // Browse mode: reset cursor stack + count + load page 1.
      cursorsRef.current = [null];
      setPage(1);
      setDocs([]);
      refreshBrowseCount();
      fetchAtBrowse(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schema.key, search, statusFilter, isSearchMode]);

  // ─── Public methods ────────────────────────────────────────────────

  const goToPage = useCallback(
    (n) => {
      const target = Math.min(Math.max(1, n), totalPages);
      if (target === page && !isLoading) return;
      if (isSearchMode) fetchAtSearch(target);
      else fetchAtBrowse(target);
    },
    [
      fetchAtBrowse,
      fetchAtSearch,
      page,
      totalPages,
      isLoading,
      isSearchMode,
    ],
  );

  const refresh = useCallback(async () => {
    // Invalidate the search cache so we re-pull fresh data, then reload
    // whichever mode we're currently in.
    allDocsRef.current = null;
    if (isSearchMode) {
      await fetchAtSearch(1);
    } else {
      const newCount = await refreshBrowseCount();
      const effectiveCount = newCount ?? totalCount;
      const newTotalPages = Math.max(
        1,
        Math.ceil(effectiveCount / pageSize),
      );
      const targetPage = Math.min(page, newTotalPages);
      await fetchAtBrowse(targetPage);
    }
  }, [
    isSearchMode,
    fetchAtSearch,
    refreshBrowseCount,
    fetchAtBrowse,
    page,
    totalCount,
    pageSize,
  ]);

  const removeDoc = useCallback(
    (id) => {
      // Update the search cache so deleted docs disappear from
      // subsequent searches without forcing a re-fetch.
      if (allDocsRef.current) {
        allDocsRef.current.docs = allDocsRef.current.docs.filter(
          (d) => d.id !== id,
        );
      }
      // Update the visible page locally.
      setDocs((prev) => {
        const next = prev.filter((d) => d.id !== id);
        if (!isSearchMode && next.length === 0 && page > 1) {
          Promise.resolve().then(() => fetchAtBrowse(page - 1));
        }
        return next;
      });
      setTotalCount((c) => Math.max(0, c - 1));
    },
    [isSearchMode, page, fetchAtBrowse],
  );

  return {
    docs,
    page,
    totalCount,
    totalPages,
    pageSize,
    isLoading,
    isPaging,
    error,
    goToPage,
    refresh,
    removeDoc,
    // Useful for the UI to show "X all-mode results" vs "X matching"
    isSearchMode,
  };
}
